import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Typography,
  Chip,
  Button,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  IconButton,
  Grid2,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";

import PageHeader from "../components/common/PageHeader";
import SearchFilters from "../components/common/SearchFilters";
import projectService from "../services/projectService";
import {
  Project,
  ProjectFilter,
  PaginationParams,
} from "../services/projectService";
import CommonPagination from "../components/common/CommonPagination";
import RangeDateFilter from "../components/common/Filters/RangeDateFilter";
import ChipFilter from "../components/common/Filters/ChipFilter";
import SearchInput from "../components/common/Filters/SearchInput";
import SingleSelectFilter from "../components/common/Filters/SingleSelectFilter";
import FilterGrid2 from "../components/common/Filters/FilterGrid2";
import SearchButton from "../components/common/Filters/SearchButton";
import AddButton from "../components/common/Filters/AddButton";
// สถานะโครงการ
enum ProjectStatus {
  NOT_STARTED = "NOT_STARTED",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
}

interface Column {
  field: string;
  headerName: string;
  width?: number;
  align?: "left" | "center" | "right";
  minWidth?: number;
  flex?: number;
  hideable?: boolean;
  renderCell?: (params: { row: Project }) => React.ReactNode;
}

const Projects: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState<boolean>(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [statusFilters, setStatusFilters] = useState<ProjectFilter[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [searchKeyword, setSearchKeyword] = useState<string>("");

  // Pagination state
  const [pagination, setPagination] = useState<{
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
  }>({
    page: 1,
    perPage: 10,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    fetchProjects();
  }, [pagination.page, pagination.perPage]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const params: PaginationParams = {
        page: pagination.page,
        perPage: pagination.perPage,
      };

      const data = await projectService.getProjects(params);
      setProjects(data.projects);
      setStatusFilters([...data.filters.status]);

      // Update pagination from response
      setPagination((prev) => ({
        ...prev,
        total: data.pagination.total,
        totalPages: data.pagination.totalPages,
      }));

      setLoading(false);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const params: PaginationParams = {
        page: 1, // Reset to first page on search
        perPage: pagination.perPage,
      };

      const data = await projectService.searchProjects(
        selectedStatus,
        searchKeyword,
        params
      );
      setProjects(data.projects);

      // Update pagination from response
      setPagination((prev) => ({
        ...prev,
        page: 1,
        total: data.pagination.total,
        totalPages: data.pagination.totalPages,
      }));

      setLoading(false);
    } catch (error) {
      console.error("Error searching projects:", error);
      setLoading(false);
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPagination((prev) => ({
      ...prev,
      page: value,
    }));
  };

  const handlePerPageChange = (perPage: number) => {
    setPagination((prev) => ({
      ...prev,
      page: 1, // Reset to first page when changing items per page
      perPage,
    }));
  };

  const handleAddProject = () => {
    navigate("/projects/add");
  };

  const handleEditProject = (id: number | undefined, objectId: string) => {
    if (id === undefined) return;
    navigate(`/projects/${objectId}/edit`);
  };

  const handleViewProjectDetails = (
    id: number | undefined,
    objectId: string
  ) => {
    if (id === undefined) return;
    navigate(`/projects/${objectId}`);
  };

  const handleManageProject = (id: number, objectId: string) => {
    navigate(`/projects/${objectId}/manage`);
  };

  const columns: Column[] = [
    {
      field: "id",
      headerName: "ลำดับ",
      width: 70,
      align: "center" as const,
      minWidth: 50,
    },
    {
      field: "projectCode",
      headerName: "รหัสโครงการ",
      width: 120,
      align: "center" as const,
      minWidth: 100,
    },
    {
      field: "projectName",
      headerName: "ชื่อโครงการ",
      width: 250,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "workingTime",
      headerName: "เวลาทำงาน",
      width: 180,
      align: "center" as const,
      minWidth: 150,
      hideable: true,
    },
    {
      field: "status",
      headerName: "สถานะ",
      width: 150,
      align: "center" as const,
      minWidth: 120,
      renderCell: (params: { row: Project }) => {
        let color = "default";
        let label = params.row.status;

        if (params.row.status === ProjectStatus.IN_PROGRESS) {
          color = "success";
          label = "กำลังดำเนินการ";
        } else if (params.row.status === ProjectStatus.COMPLETED) {
          color = "info";
          label = "เสร็จสิ้น";
        } else if (params.row.status === ProjectStatus.NOT_STARTED) {
          color = "warning";
          label = "ยังไม่เริ่ม";
        }

        return (
          <Typography
            variant="body2"
            sx={{
              color:
                color === "success"
                  ? "#2e7d32"
                  : color === "info"
                  ? "#0288d1"
                  : color === "warning"
                  ? "#ed6c02"
                  : "#666",
              py: 0.5,
              px: 1.5,
              display: "inline-block",
              fontSize: isMobile ? "0.75rem" : "0.875rem",
              fontWeight: 500,
            }}
          >
            {label}
          </Typography>
        );
      },
    },
    {
      field: "actions",
      headerName: "จัดการ",
      width: 100,
      align: "center" as const,
      minWidth: 100,
      renderCell: (params: { row: Project }) => (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <IconButton
            onClick={() =>
              handleViewProjectDetails(params.row.id, params.row.objectId)
            }
            sx={{
              color: "success.main",
              cursor: "pointer",
              "&:hover": { color: "success.dark" },
            }}
          >
            <Box
              component="img"
              src="/images/icons/button-detail.png"
              alt="View"
              sx={{ width: 32, height: 32 }}
            />
          </IconButton>
          <IconButton
            onClick={() =>
              handleEditProject(params.row.id, params.row.objectId)
            }
            sx={{
              color: "info.main",
              cursor: "pointer",
              "&:hover": { color: "info.dark" },
            }}
          >
            <Box
              component="img"
              src="/images/icons/button-modify.png"
              alt="View"
              sx={{ width: 32, height: 32 }}
            />
          </IconButton>
        </Box>
      ),
    },
  ];

  const searchFilters = [
    {
      label: "สถานะโครงการ",
      value: "status",
      options: statusFilters,
      onChange: (value: string) => setSelectedStatus(value),
    },
  ];

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: "100%",
      }}
    >
      <PageHeader title="รายชื่อโครงการ" />

      <FilterGrid2>
        <Grid2 size={{ xs: 12, sm: 3 }}>
          <SearchInput
            placeholder="ค้นหาโครงการ"
            autoChange
            value={searchKeyword}
            onChange={(value) => setSearchKeyword(value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 2 }}>
          <SingleSelectFilter
            showAllOption={false}
            filter={{
              label: "สถานะโครงการ",
              value: "status",
              options: statusFilters.map((filter) => ({
                value: filter.value,
                label: filter.label,
              })),
            }}
            value={selectedStatus}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSelectedStatus(event.target.value);
            }}
          />
        </Grid2>
        <Grid2>
          <SearchButton onClick={handleSearch} />
        </Grid2>
        <Grid2 sx={{ marginLeft: { xs: 0, md: 0, lg: "auto" } }}>
          <AddButton onClick={handleAddProject} label="เพิ่มโครงการ" />
        </Grid2>
      </FilterGrid2>

      <Paper sx={{ width: "100%", overflow: "hidden", mt: 2 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 400,
            }}
          >
            <CircularProgress />
          </Box>
        ) : projects.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 400,
            }}
          >
            <Typography variant="h6" color="text.secondary">
              ไม่พบข้อมูลโครงการ
            </Typography>
          </Box>
        ) : (
          <>
            <Paper sx={{ width: "100%", overflow: "hidden" }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell
                          key={column.field}
                          align={column.align}
                          style={{ minWidth: column.minWidth }}
                        >
                          {column.headerName}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projects.map((project) => (
                      <TableRow key={project.id}>
                        {columns.map((column) => (
                          <TableCell key={column.field} align={column.align}>
                            {column.renderCell
                              ? column.renderCell({ row: project })
                              : String(
                                  project[column.field as keyof Project] || ""
                                )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
            
            {/* Custom Pagination */}
            <CommonPagination
              count={Math.ceil(pagination.total / pagination.perPage)}
              page={pagination.page}
              onChange={handlePageChange}
              rowsPerPage={pagination.perPage}
              onRowsPerPageChange={handlePerPageChange}
              totalItems={pagination.total}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default Projects;
